// Shared footer actions for the V2 order view (workspace tab footer and the
// history order modal): accept/reject, PIN-escalated cancel and the print
// menu items/handler. Host-specific concerns stay in the components: the
// drafts-coupled edit flow, auto-close watchers and the print-menu open/close
// UI state (each host closes its own menu before delegating to handlePrint).
//
// After a successful accept/reject/cancel the order instance is best-effort
// refreshed: history orders are usually NOT in the Pinia store, so socket
// updates never reach the fetched snapshot. refreshOrder Object.assigns
// not-in-store instances in place and has a stale-guard for store instances,
// so this is safe in both hosts.
import { computed, getCurrentInstance, ref, Ref } from 'vue';
import OrdersService from '@/api/services/orders.service';
import ModalAdminPin from '@/components/modals/ModalAdminPin.vue';
import { maybePromptCoversAfterAccept } from '@/composition/covers-accept-prompt';
import { useAuth } from '@/composition/use-auth';
import { useModal } from '@/composition/use-modal';
import { useOrders } from '@/composition/use-orders';
import { usePrinting } from '@/composition/use-printing';
import { useUi } from '@/composition/use-ui';
import { OrderStatus } from '@/enums/OrderEnums';
import { PermissionType } from '@/enums/PermissionType';
import { AlertType } from '@/interfaces/IAlert';
import { IAuthData } from '@/interfaces/IAuthData';
import { IOrder } from '@/interfaces/IOrder';
import { PrinterType } from '@/modules/PrintService';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { getPaidOrderCancelDeclineGuardError } from '@/utils/orders.utils';
import { IManualPrintGate } from '@/utils/print-gate.utils';

export interface IPrintMenuItem {
  labelKey: string;
  icon: string;
  printerTypes: PrinterType[];
}

// Same statuses the old modal offered "Cancelar" for (Received has its own
// Aceptar/Rechazar pair instead).
const CANCELABLE_STATUSES = [
  OrderStatus.Progress,
  OrderStatus.ReadyToTake,
  OrderStatus.Delivering,
  OrderStatus.Finished,
];

// Statuses the edit flow blocks (mirrors the workspace's handleEdit trio;
// posDrafts.openOrderTab has no status guard of its own).
const EDIT_BLOCKED_STATUSES = [
  OrderStatus.Canceled,
  OrderStatus.Declined,
  OrderStatus.Finished,
];

export function useOrderViewActions(order: Ref<IOrder>) {
  const ui = useUi();
  const modals = useModal();
  const auth = useAuth();
  const printing = usePrinting();
  const ordersHelper = useOrders();
  const restaurantStore = useRestaurantStore();

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  //#region Status computeds

  const isReceived = computed(
    () => order.value.status === OrderStatus.Received
  );

  const isCanceledOrDeclined = computed(() => {
    const s = order.value.status;
    return s === OrderStatus.Canceled || s === OrderStatus.Declined;
  });

  const canCancel = computed(() =>
    CANCELABLE_STATUSES.includes(order.value.status)
  );

  const isEditBlocked = computed(() =>
    EDIT_BLOCKED_STATUSES.includes(order.value.status)
  );

  //#endregion

  //#region Status transitions (accept / reject / cancel)

  const updating = ref(false);

  async function refreshBestEffort(): Promise<void> {
    try {
      await ordersHelper.refreshOrder({ order: order.value });
    } catch (error) {
      console.error('Error refreshing order', error);
    }
  }

  async function setStatusViaHelper(status: OrderStatus): Promise<boolean> {
    if (updating.value) return false;

    updating.value = true;
    try {
      const didUpdate = await ordersHelper.setOrderStatus(order.value, status);
      if (didUpdate) await refreshBestEffort();
      return didUpdate;
    } catch (error) {
      ui.serverError(error, i18nT('posV2.statusUpdateError'));
      return false;
    } finally {
      updating.value = false;
    }
  }

  async function handleAccept(): Promise<void> {
    const didUpdate = await setStatusViaHelper(OrderStatus.Progress);
    if (didUpdate) {
      // Fire-and-forget covers confirmation when this accept opened the table.
      void maybePromptCoversAfterAccept(proxy.$modal, order.value);
    }
  }

  async function handleReject(): Promise<void> {
    if (updating.value) return;

    const confirmed = await modals.confirm({
      title: i18nT('posV2.rejectOrderTitle'),
      message: i18nT('posV2.rejectOrderConfirm'),
    });
    if (!confirmed) return;

    await setStatusViaHelper(OrderStatus.Declined);
  }

  // PIN escalation + direct service call, replicating the old modal
  // (useOrders().setOrderStatus has no authData param).
  async function handleCancel(): Promise<void> {
    if (updating.value) return;
    const o = order.value;

    let authData: IAuthData | undefined;
    if (!auth.hasPermission(PermissionType.OrdersCancel)) {
      authData = await modals.showDynamicDialog<IAuthData>(
        ModalAdminPin,
        {
          requiredPermission: PermissionType.OrdersCancel,
          reason: 'order-cancel',
        },
        'admin-pin'
      );
      if (!authData) return;
    }

    const confirmed = await modals.confirm({
      title: i18nT('posV2.cancelOrder'),
      message: i18nT('posV2.cancelOrderConfirm'),
    });
    if (!confirmed) return;

    const paidGuardError = getPaidOrderCancelDeclineGuardError({
      order: o,
      status: OrderStatus.Canceled,
    });
    if (paidGuardError) {
      ui.showToast(AlertType.Error, paidGuardError);
      return;
    }

    updating.value = true;
    try {
      await OrdersService.setStatus(
        o.id,
        OrderStatus.Canceled,
        restaurantStore.caja?.id,
        authData
      );
      await refreshBestEffort();
    } catch (error) {
      ui.serverError(error, i18nT('posV2.statusUpdateError'));
      ordersHelper.refreshOrder({ order: o }).catch((err) => {
        console.error('Error refreshing order', err);
      });
    } finally {
      updating.value = false;
    }
  }

  //#endregion

  //#region Print menu items + handler (menu open/close UI stays in the host)

  const printMenuItems: IPrintMenuItem[] = [
    {
      labelKey: 'posV2.printTicket',
      icon: 'receipt',
      printerTypes: [PrinterType.Ticket],
    },
    {
      labelKey: 'posV2.printComanda',
      icon: 'clipboard-list-outline',
      printerTypes: [PrinterType.Comanda],
    },
    {
      labelKey: 'posV2.printEtiquetas',
      icon: 'tag-text',
      printerTypes: [PrinterType.Etiqueta],
    },
    {
      labelKey: 'posV2.printAll',
      icon: 'printer',
      printerTypes: [
        PrinterType.Ticket,
        PrinterType.Comanda,
        PrinterType.Etiqueta,
      ],
    },
  ];

  type TGatedPrinterType = PrinterType.Comanda | PrinterType.Etiqueta;

  function isGatedPrinterType(type: PrinterType): type is TGatedPrinterType {
    return type === PrinterType.Comanda || type === PrinterType.Etiqueta;
  }

  function reprintMessageKey(types: TGatedPrinterType[]): string {
    const hasComanda = types.includes(PrinterType.Comanda);
    const hasEtiqueta = types.includes(PrinterType.Etiqueta);
    if (hasComanda && hasEtiqueta) return 'posV2.reprintComandaEtiquetasMsg';
    return hasComanda ? 'posV2.reprintComandaMsg' : 'posV2.reprintEtiquetasMsg';
  }

  async function handlePrint(item: IPrintMenuItem): Promise<void> {
    const what = i18nT(item.labelKey);

    // Snapshot the order and compute every gate BEFORE any await: the
    // injected order ref can change while the confirmation dialog is open.
    const o = order.value;

    const gates = new Map<TGatedPrinterType, IManualPrintGate>();
    for (const printerType of item.printerTypes) {
      if (isGatedPrinterType(printerType)) {
        gates.set(printerType, printing.getManualPrintGate(o, printerType));
      }
    }

    const typesNeedingConfirm = Array.from(gates.entries())
      .filter(([, gate]) => gate.state === 'reprint')
      .map(([type]) => type);

    let reprintConfirmed = false;
    if (typesNeedingConfirm.length > 0) {
      reprintConfirmed = await modals.confirm({
        title: i18nT('posV2.reprintTitle'),
        message: i18nT(reprintMessageKey(typesNeedingConfirm)),
      });
    }

    for (const printerType of item.printerTypes) {
      const gate = isGatedPrinterType(printerType)
        ? gates.get(printerType)
        : undefined;

      // Ticket: ungated, identical to today.
      if (!gate) {
        printing.printOrder({ printerType, order: o }).catch((error) => {
          ui.serverError(error, i18nT('posV2.printError', { what }));
        });
        continue;
      }

      if (gate.state === 'unprinted') {
        printing
          .printOrder({ printerType, order: o, printMode: 'unprinted' })
          .catch((error) => {
            ui.serverError(error, i18nT('posV2.printError', { what }));
          });
      } else if (gate.state === 'reprint') {
        if (!reprintConfirmed) continue;
        // Explicit itemsToPrint (the gate's eligible set) instead of
        // printMode 'all': raw items include canceled/unlabeled ones and the
        // sticker path would print labels for them.
        printing
          .printOrder({
            printerType,
            order: o,
            itemsToPrint: gate.reprintItems,
            variant:
              printerType === PrinterType.Comanda ? 'reprint' : undefined,
          })
          .catch((error) => {
            ui.serverError(error, i18nT('posV2.printError', { what }));
          });
      } else if (item.printerTypes.length === 1) {
        // 'nothing' as the only requested kind; inside "Todo" skip silently.
        ui.showToast(AlertType.Info, i18nT('posV2.nothingToPrint'));
      }
    }
  }

  //#endregion

  return {
    updating,
    isReceived,
    isCanceledOrDeclined,
    canCancel,
    isEditBlocked,
    handleAccept,
    handleReject,
    handleCancel,
    printMenuItems,
    handlePrint,
  };
}
